import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ConcepteIcon } from './IconMap';
import { useAppContext } from '../AppContext';
import {
  buildFullTreeWithEntities,
  resolveMovementConceptAndPath
} from '../../services/concepteGraphService';

/**
 * Component reutilitzable per visualitzar el concepte d'un moviment o subdivisió.
 * Utilitza exclusivament els IDs de node com a font de veritat, i calcula
 * la ruta visualment sense dependre de conceptePath ni de cerques ambigües.
 */
const ConcepteMovementDisplay = ({
  movement,
  pathInfo = null,
  enrichedTree: propEnrichedTree = null,
  maxWidth = '220px',
  showWarningParent = true
}) => {
  const { conceptesTree, entitatsTransversals, conceptesNodesMap } = useAppContext();

  const computedInfo = useMemo(() => {
    if (!movement) {
      return {
        conceptName: '',
        displayPath: '',
        fullBreadcrumb: '',
        isAssignedToParent: false
      };
    }

    // Si ja rebem pathInfo precalculat (ex. a la llista massiva de moviments)
    if (pathInfo) {
      return {
        conceptName: pathInfo.conceptName || movement.concepte || 'PENDENT',
        conceptCode: pathInfo.conceptCode || movement.codi || movement.concepte || '',
        nodeId: pathInfo.nodeId || null,
        displayPath: pathInfo.displayPath || '',
        fullBreadcrumb: pathInfo.fullBreadcrumb || '',
        isAssignedToParent: Boolean(pathInfo.isAssignedToParent)
      };
    }

    // Càlcul basat exclusivament en IDs de nodes
    const activeTree = propEnrichedTree || buildFullTreeWithEntities(conceptesTree, entitatsTransversals);
    return resolveMovementConceptAndPath(movement, activeTree, conceptesNodesMap);
  }, [movement, pathInfo, propEnrichedTree, conceptesTree, entitatsTransversals, conceptesNodesMap]);

  if (!movement) return null;

  const { conceptName, conceptCode, displayPath, fullBreadcrumb, isAssignedToParent } = computedInfo;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <ConcepteIcon concept={conceptName} code={conceptCode} />
        {showWarningParent && isAssignedToParent && (
          <Tooltip title="Avís: Aquest moviment està associat directament a un concepte pare en comptes d'un concepte fill">
            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />
          </Tooltip>
        )}
      </Box>
      {displayPath && (
        <Tooltip title={`Ruta Jeràrquica: ${fullBreadcrumb || displayPath}`}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth
            }}
          >
            {displayPath}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

export default ConcepteMovementDisplay;
